import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, Trash2 } from "lucide-react";
import { AdminTable } from "./lib";

// Actions holds two buttons now (reset password, delete), hence 92px rather than 60.
const GRID = "grid-cols-[minmax(150px,1fr)_minmax(190px,1.2fr)_130px_90px_80px_80px_92px]";
const COLUMNS = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "bar", label: "Bar ID" },
  { key: "assigned", label: "Assigned" },
  { key: "open", label: "Open" },
  { key: "closed", label: "Closed" },
  { key: "actions", label: "" },
];

export function AdminReviewers() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  // No password field: an advocate is enrolled on the platform's starting password and must replace
  // it at first sign-in (backend core/config.py DEFAULT_ADVOCATE_PASSWORD).
  const [form, setForm] = useState({ name:"", email:"", mobile:"", bar_council_id:"" });
  const [open, setOpen] = useState(false);
  // What to hand over, kept on screen after the dialog closes rather than announced in a toast that
  // vanishes in four seconds — admin has to read this out or type it into an email.
  const [handover, setHandover] = useState(null); // { name, password }
  const load = () => api.get("/admin/reviewers").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    try {
      const { data } = await api.post("/admin/reviewers", form);
      toast.success("Reviewer created");
      setHandover({ name: form.name || form.email, password: data.temporary_password });
      setOpen(false);
      setForm({name:"",email:"",mobile:"",bar_council_id:""});
      load();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const resetPw = async (r) => {
    if (!window.confirm(`Reset ${r.name}'s password? Their current one stops working immediately.`)) return;
    try {
      const { data } = await api.post(`/admin/reviewers/${r.id}/reset-password`);
      setHandover({ name: r.name, password: data.temporary_password });
      toast.success("Password reset");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if(!window.confirm("Delete?")) return; await api.delete(`/admin/reviewers/${id}`); toast.success("Deleted"); load(); };
  return (
    <div className="space-y-6" data-testid="admin-reviewers-page">
      <div className="flex items-center justify-between">
        <h1 className="font-serif-display text-3xl md:text-4xl text-white">Reviewers</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="create-reviewer-btn" className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]">+ Add Reviewer</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>Create Reviewer</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {["name","email","mobile","bar_council_id"].map(f => (
                <div key={f}><Label className="capitalize">{f.replace(/_/g," ")}</Label><Input data-testid={`rev-${f}`} type="text" value={form[f]} onChange={e=>setForm({...form,[f]:e.target.value})}/></div>
              ))}
              <p className="text-xs text-[#A0AABF] leading-relaxed">
                You don't set a password. The advocate is created with a temporary one, shown to you
                once this is saved, and they must replace it before the portal will do anything
                for them.
              </p>
              <Button data-testid="rev-submit" onClick={create} className="w-full bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]">Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {handover && (
        <div data-testid="reviewer-handover"
             className="rounded-xl border border-[#CBA96C]/40 bg-[#CBA96C]/10 px-4 py-3 flex items-start justify-between gap-4">
          <div className="text-sm text-[#F9F8F6]">
            <div className="font-medium">Temporary password for {handover.name}</div>
            <div className="mt-1 text-[#A0AABF] text-xs leading-relaxed">
              Give them <code data-testid="handover-password"
                              className="px-1.5 py-0.5 rounded bg-[#080C16] text-[#CBA96C] font-mono">{handover.password}</code>{" "}
              to sign in with. They'll be asked to choose their own straight away, and cannot use
              the portal until they do.
            </div>
          </div>
          <button data-testid="dismiss-handover" onClick={() => setHandover(null)}
                  className="text-xs text-[#A0AABF] hover:text-white shrink-0 pt-0.5">Dismiss</button>
        </div>
      )}
      <AdminTable
        testid="admin-reviewers" columns={COLUMNS} gridCols={GRID} rows={rows}
        defaultSort="name" empty="No reviewers yet."
        searchPlaceholder="Search name, email, bar ID…"
        matches={(r, q) => [r.name, r.email, r.bar_council_id, r.mobile].filter(Boolean).join(" ").toLowerCase().includes(q)}
        accessors={{
          name: (r) => (r.name || "").toLowerCase(),
          email: (r) => (r.email || "").toLowerCase(),
          bar: (r) => r.bar_council_id || "",
          assigned: (r) => r.assigned_count ?? 0,
          open: (r) => r.open_count ?? 0,
          closed: (r) => r.closed_count ?? 0,
        }}
        renderRow={(r) => (
          <>
            <button onClick={() => nav(`/admin/reviewers/${r.id}`)} data-testid={`open-reviewer-${r.id}`}
                    className="text-sm text-white truncate pt-1.5 text-left hover:text-[#CBA96C] transition-colors">
              {r.name}
            </button>
            <div className="text-xs text-[#A0AABF] truncate pt-1.5 flex items-center gap-1.5">
              <span className="truncate">{r.email}</span>
              {/* Who is still on the password they were handed — the starting one is the same for
                  every advocate, so an account left sitting on it is one anybody could walk into. */}
              {r.must_change_password && (
                <span data-testid={`rev-temp-pw-${r.id}`} title="Has not chosen a password yet"
                      className="shrink-0 text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-[#F5A524]/15 text-[#F5A524] border border-[#F5A524]/30">
                  temp
                </span>
              )}
            </div>
            <div className="text-xs text-[#A0AABF] truncate pt-1.5">{r.bar_council_id || "—"}</div>
            <div className="text-sm text-white pt-1.5">{r.assigned_count ?? 0}</div>
            {/* Open caseload is the number that decides who gets the next assignment */}
            <div className="text-sm text-[#CBA96C] pt-1.5">{r.open_count ?? 0}</div>
            <div className="text-sm text-[#A0AABF] pt-1.5">{r.closed_count ?? 0}</div>
            <div className="pt-1 flex items-center gap-0.5">
              <button data-testid={`reset-rev-${r.id}`} onClick={() => resetPw(r)} title="Reset password"
                      aria-label="Reset password" className="p-1.5 rounded-full text-[#6B7280] hover:text-[#CBA96C] hover:bg-white/5 transition-colors">
                <KeyRound className="w-3.5 h-3.5"/>
              </button>
              <button data-testid={`del-rev-${r.id}`} onClick={() => del(r.id)} title="Delete reviewer"
                      aria-label="Delete reviewer" className="p-1.5 rounded-full text-[#6B7280] hover:text-[#F27373] hover:bg-white/5 transition-colors">
                <Trash2 className="w-3.5 h-3.5"/>
              </button>
            </div>
          </>
        )}
      />
    </div>
  );
}
